/**
 * Categories Sidebar Component
 * Displays 17 detection categories with expand/collapse, lazy-loaded items,
 * category-level and item-level checkboxes driven by Firestore status.
 * Selected = will be redacted. Deselected = kept visible. Remove = deleted permanently.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { updatePiiItemStatus, updatePiiItemsBatch } from '../../services/cloudFunctions';
import {
  ChevronDown,
  ChevronRight,
  X,
  Loader,
  AlertCircle,
} from 'lucide-react';

// Category display order and metadata
const CATEGORIES = [
  { key: 'person_names',       label: 'Person Names',       source: 'haiku' },
  { key: 'email_addresses',    label: 'Email Addresses',    source: 'regex' },
  { key: 'phone_numbers',      label: 'Phone Numbers',      source: 'regex' },
  { key: 'dates',              label: 'Dates',              source: 'regex' },
  { key: 'physical_addresses', label: 'Physical Addresses', source: 'haiku' },
  { key: 'id_numbers',         label: 'SA ID Numbers',      source: 'regex' },
  { key: 'account_numbers',    label: 'Account Numbers',    source: 'regex' },
  { key: 'credit_cards',       label: 'Credit Cards',       source: 'regex' },
  { key: 'urls',               label: 'URLs',               source: 'regex' },
  { key: 'ip_addresses',       label: 'IP Addresses',       source: 'regex' },
  { key: 'company_names',      label: 'Company Names',      source: 'haiku' },
  { key: 'financial_figures',  label: 'Financial Figures',   source: 'haiku' },
  { key: 'commercial_terms',   label: 'Commercial Terms',   source: 'haiku' },
  { key: 'demographic',        label: 'Demographic Info',   source: 'haiku' },
  { key: 'medical_health',     label: 'Medical/Health',     source: 'haiku' },
  { key: 'genetic_data',       label: 'Genetic Data',       source: 'haiku' },
  { key: 'identifiers',        label: 'Identifiers',        source: 'haiku' },
];

// Color palette for categories (matches display order)
const CATEGORY_COLORS = {
  person_names:       { bg: 'rgba(255, 193, 7, 0.3)',  border: '#ffc107' },
  email_addresses:    { bg: 'rgba(33, 150, 243, 0.3)', border: '#2196f3' },
  phone_numbers:      { bg: 'rgba(156, 39, 176, 0.3)', border: '#9c27b0' },
  dates:              { bg: 'rgba(255, 152, 0, 0.3)',  border: '#ff9800' },
  physical_addresses: { bg: 'rgba(76, 175, 80, 0.3)',  border: '#4caf50' },
  id_numbers:         { bg: 'rgba(244, 67, 54, 0.3)',  border: '#f44336' },
  account_numbers:    { bg: 'rgba(0, 150, 136, 0.3)',  border: '#009688' },
  credit_cards:       { bg: 'rgba(233, 30, 99, 0.3)',  border: '#e91e63' },
  urls:               { bg: 'rgba(63, 81, 181, 0.3)',  border: '#3f51b5' },
  ip_addresses:       { bg: 'rgba(96, 125, 139, 0.3)', border: '#607d8b' },
  company_names:      { bg: 'rgba(255, 87, 34, 0.3)',  border: '#ff5722' },
  financial_figures:  { bg: 'rgba(121, 85, 72, 0.3)',  border: '#795548' },
  commercial_terms:   { bg: 'rgba(0, 188, 212, 0.3)',  border: '#00bcd4' },
  demographic:        { bg: 'rgba(139, 195, 74, 0.3)', border: '#8bc34a' },
  medical_health:     { bg: 'rgba(233, 30, 99, 0.3)',  border: '#e91e63' },
  genetic_data:       { bg: 'rgba(171, 71, 188, 0.3)', border: '#ab47bc' },
  identifiers:        { bg: 'rgba(103, 58, 183, 0.3)', border: '#673ab7' },
};

export function getCategoryColor(categoryKey) {
  return CATEGORY_COLORS[categoryKey] || { bg: 'rgba(158, 158, 158, 0.3)', border: '#9e9e9e' };
}

export default function CategoriesSidebar({
  documentId,
  userId,
  categorySummary,
  hoveredPii,
  setHoveredPii,
  currentPage,
  setCurrentPage,
}) {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [categoryItems, setCategoryItems] = useState({});       // { categoryKey: [items] }
  const [loadingCategories, setLoadingCategories] = useState({}); // { categoryKey: true }
  const listenersRef = useRef(new Map()); // categoryKey → unsubscribe fn
  const [manualItems, setManualItems] = useState([]);
  const [manualExpanded, setManualExpanded] = useState(false);

  // Optimistic UI state
  const [pendingItems, setPendingItems] = useState(new Map());       // itemId -> optimistic status
  const [pendingCategories, setPendingCategories] = useState(new Set()); // category keys in-flight
  const [error, setError] = useState(null);                           // error banner text
  const errorTimerRef = useRef(null);
  const [bulkActionPending, setBulkActionPending] = useState(false);

  // Returns effective status: optimistic overlay takes precedence over Firestore
  const getEffectiveStatus = useCallback((itemId, firestoreStatus) => {
    return pendingItems.has(itemId) ? pendingItems.get(itemId) : firestoreStatus;
  }, [pendingItems]);

  // Show error with auto-dismiss
  const showError = useCallback((message) => {
    setError(message);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setError(null), 3000);
  }, []);

  // Reset all listeners and optimistic state when document changes
  useEffect(() => {
    setPendingItems(new Map());
    setPendingCategories(new Set());
    setError(null);
    return () => {
      listenersRef.current.forEach(fn => fn());
      listenersRef.current.clear();
    };
  }, [documentId]);

  // Cleanup error timer on unmount
  useEffect(() => {
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, []);

  // Always-on listener for manual redaction items added while in categories mode
  useEffect(() => {
    if (!userId || !documentId) return;
    const q = query(
      collection(db, 'piiItems'),
      where('userId', '==', userId),
      where('documentId', '==', documentId),
      where('detectionMethod', '==', 'manual')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(item => item.status !== 'approved');
      items.sort((a, b) => (a.page || 1) - (b.page || 1));
      setManualItems(items);
    });
    return () => unsubscribe();
  }, [userId, documentId]);

  // Toggle category expand/collapse, lazy load items
  const toggleCategory = useCallback((categoryKey) => {
    setExpandedCategories(prev => {
      const next = { ...prev, [categoryKey]: !prev[categoryKey] };
      return next;
    });
  }, []);

  // Manage per-category listeners: subscribe when expanded, unsubscribe when collapsed
  useEffect(() => {
    if (!userId || !documentId) return;
    // Subscribe to newly expanded categories
    for (const categoryKey of Object.keys(expandedCategories)) {
      if (!expandedCategories[categoryKey]) continue;
      if (listenersRef.current.has(categoryKey)) continue;

      setLoadingCategories(prev => ({ ...prev, [categoryKey]: true }));

      const q = query(
        collection(db, 'piiItems'),
        where('userId', '==', userId),
        where('documentId', '==', documentId),
        where('detectionMethod', '==', 'categories'),
        where('category', '==', categoryKey)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const items = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(item => item.status !== 'approved');
        items.sort((a, b) => (a.page || 1) - (b.page || 1));
        setCategoryItems(prev => ({ ...prev, [categoryKey]: items }));
        setLoadingCategories(prev => ({ ...prev, [categoryKey]: false }));
      }, (error) => {
        console.error(`Error loading category ${categoryKey}:`, error);
        setLoadingCategories(prev => ({ ...prev, [categoryKey]: false }));
      });

      listenersRef.current.set(categoryKey, unsubscribe);
    }

    // Unsubscribe from collapsed categories
    for (const [key, unsub] of listenersRef.current) {
      if (!expandedCategories[key]) {
        unsub();
        listenersRef.current.delete(key);
      }
    }
  }, [expandedCategories, documentId, userId]);

  // Toggle single item — optimistic UI with pending spinner
  const toggleItem = async (itemId, currentStatus) => {
    if (pendingItems.has(itemId)) return; // prevent rapid double-click

    const effectiveCurrent = getEffectiveStatus(itemId, currentStatus);
    const newStatus = effectiveCurrent === 'deselected' ? 'selected' : 'deselected';

    // Optimistic: immediately show new status
    setPendingItems(prev => {
      const next = new Map(prev);
      next.set(itemId, newStatus);
      return next;
    });

    // Fire async call
    const result = await updatePiiItemStatus(itemId, newStatus);
    if (!result.success) {
      showError(result.error || 'Failed to update item');
    }

    // Clean up: on success onSnapshot delivers truth; on failure reverts to Firestore state
    setPendingItems(prev => {
      const next = new Map(prev);
      next.delete(itemId);
      return next;
    });
  };

  // Toggle all items in a category — optimistic UI with batch call
  const toggleCategorySelect = async (categoryKey) => {
    if (pendingCategories.has(categoryKey)) return; // prevent double-click

    const items = categoryItems[categoryKey] || [];
    if (items.length === 0) return;

    const allEffectiveSelected = items.every(item =>
      getEffectiveStatus(item.id, item.status) !== 'deselected'
    );
    const newStatus = allEffectiveSelected ? 'deselected' : 'selected';

    const toUpdate = items.filter(item => {
      const effectiveSelected = getEffectiveStatus(item.id, item.status) !== 'deselected';
      return newStatus === 'selected' ? !effectiveSelected : effectiveSelected;
    });

    if (toUpdate.length === 0) return;

    // Optimistic: mark all affected items + category as pending
    setPendingItems(prev => {
      const next = new Map(prev);
      toUpdate.forEach(item => next.set(item.id, newStatus));
      return next;
    });
    setPendingCategories(prev => new Set([...prev, categoryKey]));

    // Fire batch call
    const result = await updatePiiItemsBatch(
      toUpdate.map(item => ({ piiItemId: item.id, status: newStatus }))
    );
    if (!result.success) {
      showError(result.error || 'Failed to update category items');
    }

    // Clean up
    setPendingItems(prev => {
      const next = new Map(prev);
      toUpdate.forEach(item => next.delete(item.id));
      return next;
    });
    setPendingCategories(prev => {
      const next = new Set(prev);
      next.delete(categoryKey);
      return next;
    });
  };

  // Remove item — mark as approved (consistent with POPIA sidebar pattern)
  const handleRemoveItem = async (itemId) => {
    try {
      await updatePiiItemStatus(itemId, 'approved');
    } catch (error) {
      console.error('Failed to remove item:', error);
      alert('Failed to remove item. Please try again.');
    }
  };

  // Select all categories + manual items across the entire document.
  // Queries Firestore directly so collapsed categories are included.
  const handleSelectAll = async () => {
    if (!userId || !documentId || bulkActionPending) return;
    setBulkActionPending(true);
    try {
      const categoriesQ = query(
        collection(db, 'piiItems'),
        where('userId', '==', userId),
        where('documentId', '==', documentId),
        where('detectionMethod', '==', 'categories')
      );
      const manualQ = query(
        collection(db, 'piiItems'),
        where('userId', '==', userId),
        where('documentId', '==', documentId),
        where('detectionMethod', '==', 'manual')
      );
      const [catSnap, manSnap] = await Promise.all([getDocs(categoriesQ), getDocs(manualQ)]);
      const items = [...catSnap.docs, ...manSnap.docs]
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(item => item.status === 'deselected')
        .map(item => ({ piiItemId: item.id, status: 'selected' }));
      if (items.length === 0) { setBulkActionPending(false); return; }
      const result = await updatePiiItemsBatch(items);
      if (!result.success) showError(result.error || 'Failed to select all');
    } catch (error) {
      console.error('Failed to select all:', error);
      showError('Failed to select all');
    } finally {
      setBulkActionPending(false);
    }
  };

  // Deselect all categories + manual items across the entire document.
  // Queries Firestore directly so collapsed categories are included. Skips items already removed.
  const handleDeselectAll = async () => {
    if (!userId || !documentId || bulkActionPending) return;
    setBulkActionPending(true);
    try {
      const categoriesQ = query(
        collection(db, 'piiItems'),
        where('userId', '==', userId),
        where('documentId', '==', documentId),
        where('detectionMethod', '==', 'categories')
      );
      const manualQ = query(
        collection(db, 'piiItems'),
        where('userId', '==', userId),
        where('documentId', '==', documentId),
        where('detectionMethod', '==', 'manual')
      );
      const [catSnap, manSnap] = await Promise.all([getDocs(categoriesQ), getDocs(manualQ)]);
      const items = [...catSnap.docs, ...manSnap.docs]
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(item => item.status !== 'deselected' && item.status !== 'approved')
        .map(item => ({ piiItemId: item.id, status: 'deselected' }));
      if (items.length === 0) { setBulkActionPending(false); return; }
      const result = await updatePiiItemsBatch(items);
      if (!result.success) showError(result.error || 'Failed to deselect all');
    } catch (error) {
      console.error('Failed to deselect all:', error);
      showError('Failed to deselect all');
    } finally {
      setBulkActionPending(false);
    }
  };

  const totalItems = Object.values(categorySummary || {}).reduce((sum, count) => sum + count, 0) + manualItems.length;
  const loadedItems = [...Object.values(categoryItems).flat(), ...manualItems];
  const selectedCount = loadedItems.filter(i => getEffectiveStatus(i.id, i.status) !== 'deselected').length;

  return (
    <div className="w-80 bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h2 className="font-semibold text-gray-900">Detected Categories</h2>
        <p className="text-sm text-gray-500 mt-1">
          {loadedItems.length > 0
            ? `${selectedCount} of ${loadedItems.length} items selected for redaction`
            : `${totalItems} items across ${CATEGORIES.filter(c => (categorySummary?.[c.key] || 0) > 0).length} categories`
          }
        </p>
        {/* Select All / Deselect All buttons */}
        <div className="flex gap-2 mt-2">
          <button
            onClick={handleSelectAll}
            disabled={bulkActionPending}
            className={`flex-1 text-xs font-medium px-3 py-1.5 rounded-md transition-colors flex items-center justify-center ${
              bulkActionPending
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
            }`}
          >
            {bulkActionPending && <Loader className="w-3 h-3 animate-spin mr-1" />}
            Select All
          </button>
          <button
            onClick={handleDeselectAll}
            disabled={bulkActionPending}
            className={`flex-1 text-xs font-medium px-3 py-1.5 rounded-md transition-colors flex items-center justify-center ${
              bulkActionPending
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {bulkActionPending && <Loader className="w-3 h-3 animate-spin mr-1" />}
            Deselect All
          </button>
        </div>

        {/* Error banner (auto-dismisses after 3s) */}
        {error && (
          <div className="mt-2 flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-md">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700 flex-1">{error}</p>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0"
            >
              <X className="w-3 h-3 text-red-400 hover:text-red-600" />
            </button>
          </div>
        )}
      </div>

      {/* Category list */}
      <div className="overflow-auto flex-1" style={{ maxHeight: 'calc(100vh - 420px)' }}>
        {CATEGORIES.map((cat) => {
          const count = categorySummary?.[cat.key] || 0;
          const isExpanded = expandedCategories[cat.key];
          const isLoading = loadingCategories[cat.key];
          const items = categoryItems[cat.key] || [];
          const color = getCategoryColor(cat.key);
          const allSelected = items.length > 0 && items.every(item => getEffectiveStatus(item.id, item.status) !== 'deselected');
          const someSelected = items.some(item => getEffectiveStatus(item.id, item.status) !== 'deselected');

          if (count === 0) return null;

          return (
            <div key={cat.key} className="border-b border-gray-100 last:border-b-0">
              {/* Category header */}
              <button
                onClick={() => toggleCategory(cat.key)}
                className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                )}
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color.border }}
                />
                <span className="text-sm font-medium text-gray-800 flex-1 truncate">
                  {cat.label}
                </span>
                <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full min-w-[24px] text-center">
                  {count}
                </span>
              </button>

              {/* Expanded items */}
              {isExpanded && (
                <div className="bg-gray-50/50">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader className="w-4 h-4 animate-spin text-gray-400" />
                      <span className="text-xs text-gray-400 ml-2">Loading...</span>
                    </div>
                  ) : items.length === 0 ? (
                    <div className="px-4 py-3 text-xs text-gray-400 text-center">
                      No items found
                    </div>
                  ) : (
                    <>
                      {/* Select all for category */}
                      <div className="px-4 py-1.5 border-b border-gray-100 flex items-center gap-2">
                        {pendingCategories.has(cat.key) ? (
                          <Loader className="w-4 h-4 animate-spin text-emerald-500 flex-shrink-0" />
                        ) : (
                          <input
                            type="checkbox"
                            checked={allSelected}
                            ref={el => {
                              if (el) el.indeterminate = someSelected && !allSelected;
                            }}
                            onChange={() => toggleCategorySelect(cat.key)}
                            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                        )}
                        <span className="text-xs text-gray-500">
                          Select all ({items.length})
                        </span>
                      </div>

                      {/* Individual items */}
                      <div className="divide-y divide-gray-100">
                        {items.map((item) => (
                          <CategoryItem
                            key={item.id}
                            item={item}
                            color={color}
                            isSelected={getEffectiveStatus(item.id, item.status) !== 'deselected'}
                            isPending={pendingItems.has(item.id)}
                            isHovered={hoveredPii === item.id}
                            onToggle={() => toggleItem(item.id, item.status)}
                            onHover={() => setHoveredPii(item.id)}
                            onLeave={() => setHoveredPii(null)}
                            onRemove={() => handleRemoveItem(item.id)}
                            onNavigate={() => setCurrentPage(item.page || 1)}
                            showPageNav={(item.page || 1) !== currentPage}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Manual Redactions section — items added via draw/search while in categories mode */}
        {manualItems.length > 0 && (
          <div className="border-b border-gray-100 last:border-b-0">
            <button
              onClick={() => setManualExpanded(prev => !prev)}
              className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
            >
              {manualExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              )}
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: '#3b82f6' }}
              />
              <span className="text-sm font-medium text-gray-800 flex-1 truncate">
                Manual Redactions
              </span>
              <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full min-w-[24px] text-center">
                {manualItems.length}
              </span>
            </button>

            {manualExpanded && (
              <div className="bg-gray-50/50">
                <div className="divide-y divide-gray-100">
                  {manualItems.map((item) => (
                    <CategoryItem
                      key={item.id}
                      item={item}
                      color={{ bg: 'rgba(59, 130, 246, 0.3)', border: '#3b82f6' }}
                      isSelected={getEffectiveStatus(item.id, item.status) !== 'deselected'}
                      isPending={pendingItems.has(item.id)}
                      isHovered={hoveredPii === item.id}
                      onToggle={() => toggleItem(item.id, item.status)}
                      onHover={() => setHoveredPii(item.id)}
                      onLeave={() => setHoveredPii(null)}
                      onRemove={() => handleRemoveItem(item.id)}
                      onNavigate={() => setCurrentPage(item.page || 1)}
                      showPageNav={(item.page || 1) !== currentPage}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Individual item within an expanded category.
 */
function CategoryItem({
  item,
  color,
  isSelected,
  isPending,
  isHovered,
  onToggle,
  onHover,
  onLeave,
  onRemove,
  onNavigate,
  showPageNav,
}) {
  return (
    <div
      className={`px-4 py-2 pr-8 relative transition-colors ${
        isPending ? 'opacity-60 pointer-events-none' : 'cursor-pointer'
      } ${
        isHovered ? 'bg-gray-100' : 'hover:bg-gray-50'
      }`}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={isPending ? undefined : onNavigate}
    >
      <div className="flex items-start gap-2">
        {isPending ? (
          <div className="mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0">
            <Loader className="w-3.5 h-3.5 animate-spin text-emerald-500" />
          </div>
        ) : (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            onClick={(e) => e.stopPropagation()}
            className="mt-0.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900 font-mono break-all leading-snug">
            {item.value}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-gray-400">
              Page {item.page || 1}
            </span>
            {showPageNav && (
              <span className="text-xs text-emerald-600">
                Go to page {item.page || 1}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Remove button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="absolute top-2 right-2 p-1 rounded hover:bg-red-100 transition-colors group"
        title="Remove this item"
      >
        <X className="w-3.5 h-3.5 text-gray-400 group-hover:text-red-600" />
      </button>
    </div>
  );
}
